// Opportunity Group (narrative angle `family`) taxonomy — friendly labels and a
// stable display order, mirroring the Campaign Wizard's narrative-opportunity-step.
// Shared by every flat narrative-angle picker so they all group consistently.
export const ANGLE_FAMILY_LABELS: Record<string, string> = {
  conflict: "Conflict",
  identity: "Identity",
  outcome: "Outcome",
  trust: "Trust",
  worldview: "Worldview",
  life_fit: "Life Fit",
};

const ANGLE_FAMILY_ORDER = ["conflict", "identity", "outcome", "trust", "worldview", "life_fit"];
const ANGLE_FAMILY_OTHER = "__other__";

/**
 * Group narrative angles by their Opportunity Group (`family`), in a fixed
 * sensible order with an "Other" bucket last for empty/unknown family. Angles
 * within each group are sorted alphabetically by name. Empty groups are omitted.
 */
export function groupAnglesByFamily<A extends { family?: string | null; name: string }>(
  angles: A[],
): Array<{ key: string; label: string; angles: A[] }> {
  const byFamily = new Map<string, A[]>();
  for (const a of angles) {
    const key =
      a.family && ANGLE_FAMILY_LABELS[a.family.toLowerCase()]
        ? a.family.toLowerCase()
        : ANGLE_FAMILY_OTHER;
    const list = byFamily.get(key) ?? [];
    list.push(a);
    byFamily.set(key, list);
  }
  const orderedKeys = [...ANGLE_FAMILY_ORDER, ANGLE_FAMILY_OTHER].filter((k) => byFamily.has(k));
  return orderedKeys.map((key) => ({
    key,
    label: key === ANGLE_FAMILY_OTHER ? "Other" : ANGLE_FAMILY_LABELS[key],
    angles: (byFamily.get(key) ?? []).slice().sort((x, y) => x.name.localeCompare(y.name)),
  }));
}
